/**
 * Task #559 — Step 8: banner sticky onboarding embedder.
 *
 * Compare a livello root layout quando il tenant ha almeno una API key AI
 * configurata MA non ha ancora scelto l'embedder (`tenant_settings
 * .embedder_provider IS NULL`). Non dismissibile per design: senza embedder
 * la knowledge base non funziona, l'admin DEVE scegliere prima di operare.
 *
 * CTA porta a `/dashboard/ai/routing#embedder-foundation` con highlight
 * pulsante 2s gestito dalla pagina target via hash listener.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import apiClient from '@/lib/api';

interface EmbedderStatusResponse {
  configured: boolean;
  current: 'openai' | 'gemini' | null;
  model: string | null;
  has_api_keys: boolean;
}

const EMBEDDER_STATUS_QUERY_KEY = ['tenant', 'embedder-status'] as const;

export default function EmbedderConfigAlert() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();

  const { data } = useQuery<EmbedderStatusResponse | null>({
    queryKey: EMBEDDER_STATUS_QUERY_KEY,
    queryFn: async () => {
      try {
        // apiClient already unwraps the HTTP layer and returns
        // { success, data: T, ... }; T here is EmbedderStatusResponse.
        const resp = await apiClient.get<EmbedderStatusResponse>(
          '/tenant/embedder-status',
        );
        if (!resp?.success || !resp.data) return null;
        return resp.data;
      } catch {
        // Silenzioso: in caso di errore non blocchiamo la UI con un alert
        return null;
      }
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Pre-load: nessun render finché la query non risponde almeno una volta
  if (!data) return null;

  // Mostra l'alert ogni volta che l'embedder non è ancora stato scelto
  // (configured=false), indipendentemente dalla presenza di API keys.
  const shouldShow = data.configured === false;
  if (!shouldShow) return null;

  const handleConfigure = () => {
    // Reset del dismiss di sessione → il wizard riapre subito quando AITools monta
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('embedder-wizard-dismissed');
      window.dispatchEvent(new CustomEvent('embedder-wizard:reopen'));
    }
    navigate(tenantSlug ? `/${tenantSlug}/ai-tools` : '/ai-tools');
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="embedder-config-alert"
      className="sticky top-0 z-40 w-full border-b border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-700"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex-1 text-sm text-amber-900 dark:text-amber-100">
          <strong className="font-semibold">Configura l&apos;AI prima di iniziare —</strong>{' '}
          Hai inserito una API key, ma non hai ancora scelto il tipo di Embedding.
          Senza questa scelta, knowledge base, ricerca documenti e risposte AI non
          funzioneranno.
        </div>
        <button
          type="button"
          onClick={handleConfigure}
          className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
        >
          Configura ora
        </button>
      </div>
    </div>
  );
}

// Animazione pulse di highlight applicata alla sezione foundation quando
// l'utente arriva via deep link dall'alert. Iniettata una sola volta a
// livello document per evitare dipendenze CSS file-side.
if (typeof document !== 'undefined' && !document.getElementById('embedder-pulse-style')) {
  const style = document.createElement('style');
  style.id = 'embedder-pulse-style';
  style.textContent = `
    @keyframes embedderFoundationPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.6); }
      50%      { box-shadow: 0 0 0 12px rgba(245, 158, 11, 0); }
    }
    .embedder-foundation-pulse {
      animation: embedderFoundationPulse 1s ease-out 2;
      border-radius: 0.5rem;
    }
  `;
  document.head.appendChild(style);
}
